import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import ImageInput from "@/components/ImageInput";

/**
 * Generic CRUD page.
 * Props:
 *  - title: section title
 *  - endpoint: API resource path (e.g. "/team")
 *  - fields: [{ name, label, type, required, options, placeholder, span }]
 *  - columns: [{ key, label, render? }]
 *  - testidPrefix
 *  - emptyDefaults: object for "Add new" defaults
 */
export default function CrudPage({ title, endpoint, fields, columns, testidPrefix, emptyDefaults }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {} new, item editing
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async (form) => {
    setLoading(true);
    try {
      if (editing && editing.id) {
        await api.put(`${endpoint}/${editing.id}`, form);
        toast.success(`${title} updated.`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title} added.`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">// Manage</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{title}</h1>
        </div>
        <button
          onClick={() => setEditing({ ...emptyDefaults })}
          data-testid={`${testidPrefix}-add`}
          className="btn-primary clip-bolt"
        >
          <Plus className="w-4 h-4" /> New {title.replace(/s$/, "")}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-voltage-blue/40 p-12 text-center text-voltage-muted">
          No items yet. Click &quot;New&quot; to add one.
        </div>
      ) : (
        <div className="border border-voltage-blue/30 overflow-x-auto">
          <table className="w-full font-mono text-sm">
            <thead>
              <tr className="bg-voltage-blue/10 border-b border-voltage-blue/30">
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-4 py-3 overline">{c.label}</th>
                ))}
                <th className="text-right px-4 py-3 overline w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`${testidPrefix}-row-${it.id}`} className="border-b border-voltage-blue/10 hover:bg-voltage-blue/5">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-white/90 align-top">
                      {c.render ? c.render(it) : String(it[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing(it)}
                      data-testid={`${testidPrefix}-edit-${it.id}`}
                      className="text-voltage-yellow hover:text-white p-1.5 inline-flex"
                      aria-label="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(it.id)}
                      data-testid={`${testidPrefix}-delete-${it.id}`}
                      className="text-red-400 hover:text-red-300 p-1.5 inline-flex"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <CrudFormModal
          title={editing.id ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}
          fields={fields}
          initial={editing}
          loading={loading}
          onClose={() => setEditing(null)}
          onSubmit={onSave}
          testidPrefix={testidPrefix}
        />
      )}
    </div>
  );
}

function CrudFormModal({ title, fields, initial, onSubmit, onClose, loading, testidPrefix }) {
  const [form, setForm] = useState(() => ({ ...initial }));

  const set = (name, val) => setForm((f) => ({ ...f, [name]: val }));

  const submit = (e) => {
    e.preventDefault();
    const cleaned = { ...form };
    delete cleaned.id;
    delete cleaned.created_at;
    onSubmit(cleaned);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid={`${testidPrefix}-modal`}>
      <form
        onSubmit={submit}
        className="voltage-card w-full max-w-2xl p-8 my-8 relative"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testidPrefix}-modal-close`}
          className="absolute top-4 right-4 text-voltage-muted hover:text-white"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-8">{title}</h2>

        <div className="grid grid-cols-2 gap-5">
          {fields.map((f) => (
            <div key={f.name} className={f.span === "full" ? "col-span-2" : "col-span-2 sm:col-span-1"}>
              <label className="overline mb-2 block">{f.label}{f.required && " *"}</label>
              {f.type === "textarea" ? (
                <textarea
                  required={f.required}
                  rows={3}
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body resize-none"
                />
              ) : f.type === "image" ? (
                <ImageInput
                  value={form[f.name] || ""}
                  onChange={(v) => set(f.name, v)}
                  testid={`${testidPrefix}-field-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select
                  required={f.required}
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body"
                >
                  {f.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="inline-flex items-center gap-3 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form[f.name]}
                    onChange={(e) => set(f.name, e.target.checked)}
                    data-testid={`${testidPrefix}-field-${f.name}`}
                    className="w-5 h-5 accent-voltage-yellow"
                  />
                  <span className="text-sm text-white">{f.checkboxLabel || "Yes"}</span>
                </label>
              ) : f.type === "list" ? (
                <textarea
                  rows={3}
                  value={(form[f.name] || []).join("\n")}
                  onChange={(e) => set(f.name, e.target.value.split("\n").filter((s) => s.trim() !== ""))}
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  placeholder="One item per line"
                  className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-mono text-sm resize-none"
                />
              ) : (
                <input
                  type={f.type || "text"}
                  required={f.required}
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  placeholder={f.placeholder}
                  className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testidPrefix}-modal-cancel`}
            className="btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            data-testid={`${testidPrefix}-modal-save`}
            className="btn-primary clip-bolt disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
